export const RETURN_TO_KEY = 'post-login-return-to';

const NEVER_RETURN_TO = [
  '/login',
  '/signup',
  '/reset-password',
  '/forgot-password',
  '/welcome',
  '/business-select',
];

// Browsers strip tab/newline from URLs and treat "\" as "/", so "/\t/x" and "/\x" both become the
// protocol-relative "//x". Any whitespace, control character or backslash is refused outright.
const hasUnsafeCharacter = (path: string): boolean =>
  /[\s\\]/.test(path) || [...path].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f);

const isSafeReturnPath = (path: string | null): path is string => {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return false;
  if (hasUnsafeCharacter(path)) return false;

  // React Router matches case-insensitively and ignores a trailing slash, so "/LOGIN/" is /login.
  const pathname = path.split(/[?#]/)[0].toLowerCase().replace(/\/+$/, '');
  return !NEVER_RETURN_TO.includes(pathname);
};

/** Stores `path` for the post-login redirect. An unsafe or auth-flow path leaves any stored value untouched. */
export const remember = (path: string): void => {
  if (isSafeReturnPath(path)) {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  }
};

/** Returns the stored path, or `fallback` if none is stored or it fails re-validation. Leaves the key in place. */
export const peek = (fallback: string): string => {
  const stored = sessionStorage.getItem(RETURN_TO_KEY);
  return isSafeReturnPath(stored) ? stored : fallback;
};

export const forget = (): void => {
  sessionStorage.removeItem(RETURN_TO_KEY);
};

/** Returns the stored path, or `fallback` if none is stored or it fails re-validation. Always clears the key. */
export const consume = (fallback: string): string => {
  const target = peek(fallback);
  forget();
  return target;
};
